import { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { makeStyles, useTheme, Theme } from '@material-ui/core/styles';
import { Typography, TextField, Grid, Button, Fade } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { FormattedMessage, useIntl } from 'react-intl';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import COLORS from 'config/colors';
import LocaleSelect from 'components/LocaleSelect';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    minHeight: '100vh',
  },
  localeSelect: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: 1,
  },
  logoContainer: {
    textAlign: 'center',
    paddingTop: 150,
    marginBottom: 65,

    '& img': {
      width: 200,
    },
  },
  form: {
    width: 575,
    margin: '0 auto',

    '& .MuiOutlinedInput-root': {
      width: 350,
      height: 40,
      margin: '0 25px 0 0',
    },

    '& .MuiButton-root': {
      width: 200,
    },

    [theme.breakpoints.down('xs')]: {
      width: 250,

      '& .MuiOutlinedInput-root': {
        width: 250,
        height: 40,
        margin: '0 0 5px',
      },

      '& .MuiButton-root': {
        width: 250,
      },
    },
  },
  formContainer: {
    margin: '5px 0 15px',
  },
  trackButton: {
    backgroundImage: `linear-gradient(-180deg, ${COLORS.greenNormal} 0%, ${COLORS.greenDark} 100%)`,
    color: COLORS.white,

    '&:hover': {
      backgroundImage: 'none',
      backgroundColor: COLORS.greenNormal,
    },
  },
  example: {
    fontSize: 16,
    color: COLORS.inkLight,
  },
}));

interface IHomeScreenState {
  shipmentNotFound?: boolean;
  query: string;
}

export default function HomeScreen() {
  const location: { state?: IHomeScreenState } = useLocation();
  const classes = useStyles();
  const history = useHistory();
  const [keyword, setKeyword] = useState('');
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('xs'));
  const { formatMessage } = useIntl();
  const query = location?.state?.query;

  useEffect(() => {
    setTimeout(clearAlertMessage, 5000);
  });

  function clearAlertMessage() {
    if (location.state && location.state.shipmentNotFound) {
      history.replace('/', {});
    }
  }

  async function track(e: any) {
    e.preventDefault();

    if (keyword) {
      return history.push(`/shipment-tracking/${keyword}`);
    }
  }

  const title = formatMessage({ id: 'meta.home-title' });
  const description = formatMessage({ id: 'meta.app-description' });

  return (
    <div className={classes.root}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{title}</title>
        <link rel="canonical" href="https://www.trackmyshipment.co/" />
        <meta name="description" content={description} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.trackmyshipment.co/" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://www.trackmyshipment.co/images/1-ready-to-ship.svg" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.trackmyshipment.co/" />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content="https://www.trackmyshipment.co/images/1-ready-to-ship.svg" />
      </Helmet>
      <div className={classes.localeSelect}>
        <LocaleSelect />
      </div>
      <div className={classes.logoContainer}>
        <img
          src="/images/track-my-shipment-logo.svg"
          alt={formatMessage({ id: 'home.img-alt' })}
        />
      </div>
      <div className={classes.form}>
        <Typography variant="body1">
          <FormattedMessage id="home.tracking-number" />
        </Typography>
        <form onSubmit={track}>
          <Grid container direction={isNarrow ? 'column' : 'row'} className={classes.formContainer}>
            <Grid item>
              <TextField
                id="outlined-secondary"
                variant="outlined"
                color="secondary"
                onChange={(event) => setKeyword(event.target.value)}
              />
            </Grid>
            <Grid item>
              <Button variant="text" className={classes.trackButton} type="submit">
                <FormattedMessage id="home.track" />
              </Button>
            </Grid>
          </Grid>
        </form>
        {location?.state?.shipmentNotFound && (
          <Fade in={true}>
            <MuiAlert severity="error" icon={false}>
              {query ? (
                <FormattedMessage id="home.not-found" values={{ query }} />
              ) : (
                <FormattedMessage id="home.not-found-no-query" />
              )}
            </MuiAlert>
          </Fade>
        )}
        <p className={classes.example}>
          <FormattedMessage id="home.example" />
        </p>
      </div>
    </div>
  );
}
